import { formatDateSync } from './dateTimeFormat';

export const combineUniqueValues = (exams, field) => {
  if (!exams || !Array.isArray(exams)) return '';
  const values = [...new Set(exams.map((e) => e[field]).filter((v) => v))];
  return values.join('; ');
};

export const formatMultipleDates = (dateString) => {
  if (!dateString || dateString === '-') return '-';

  const dates = dateString.split(';').map((d) => d.trim()).filter((d) => d);
  if (dates.length === 0) return '-';

  const formattedDates = dates
    .map((d) => {
      try {
        return formatDateSync(d);
      } catch {
        return d;
      }
    })
    .filter((d) => d && d !== '-' && d !== 'Invalid Date');

  const uniqueFormattedDates = [...new Set(formattedDates)];
  return uniqueFormattedDates.length > 0 ? uniqueFormattedDates.join('; ') : '-';
};

export const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  try {
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

export const formatSexShort = (sex) => {
  if (!sex) return '-';
  const s = String(sex).toUpperCase();
  if (s === 'M' || s === 'MALE' || s === 'MÄNNLICH') return 'M';
  if (s === 'F' || s === 'FEMALE' || s === 'WEIBLICH') return 'F';
  if (s === 'D' || s === 'DIVERSE') return 'D';
  return sex;
};

export const isMultiDatabaseField = (combinedExams, databaseField) => {
  if (!combinedExams || combinedExams.length <= 1 || !databaseField) return false;
  const values = combinedExams.map((e) => e[databaseField] ?? '').filter((v) => v !== '');
  const unique = [...new Set(values)];
  return unique.length > 1 || ['clinical_info', 'question'].includes(databaseField);
};

export const parseTemplateFields = (fieldsJson) => {
  try {
    const parsed = typeof fieldsJson === 'string' ? JSON.parse(fieldsJson) : fieldsJson;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const buildTemplateSteps = (template, { isMultiExam, combinedExams, t }) => {
  if (!template) return [];
  const items = parseTemplateFields(template.fields_json);
  const multipleSuffix = t ? t('report.stepMultiple', '- multiple') : '- multiple';

  if (template.template_type === 'F') {
    return items.map((field, index) => {
      const isMulti = isMultiExam && isMultiDatabaseField(combinedExams, field.database_field);
      return {
        index,
        key: field.id || field.label || `field-${index}`,
        label: isMulti ? `${field.label} ${multipleSuffix}` : field.label,
        type: 'freetext-field',
        field,
        isMultiDatabase: isMulti,
      };
    });
  }

  const steps = [];
  let stepIndex = 0;
  const processedFindingIndices = new Set();

  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];
    const itemType = item.item_type || item.type;

    if (itemType === 'field') {
      const isMulti = isMultiExam && isMultiDatabaseField(combinedExams, item.database_field);
      steps.push({
        index: stepIndex++,
        key: item.id || `field-${idx}`,
        label: isMulti ? `${item.label || item.name} ${multipleSuffix}` : (item.label || item.name),
        type: 'structured-field',
        item,
        itemIndex: idx,
        isMultiDatabase: isMulti,
      });
    } else if (itemType === 'group') {
      steps.push({
        index: stepIndex++,
        key: item.id || `group-${idx}`,
        label: item.name || item.label,
        type: 'structured-group',
        item,
        itemIndex: idx,
        isMultiDatabase: false,
      });
    } else if (itemType === 'finding' && !processedFindingIndices.has(idx)) {
      const sameName = [item];
      processedFindingIndices.add(idx);
      for (let j = idx + 1; j < items.length; j++) {
        const nxt = items[j];
        if ((nxt.item_type || nxt.type) === 'finding' && nxt.name === item.name && !processedFindingIndices.has(j)) {
          sameName.push(nxt);
          processedFindingIndices.add(j);
        } else if ((nxt.item_type || nxt.type) !== 'finding') {
          break;
        }
      }
      const isMulti = isMultiExam && sameName.some((f) => isMultiDatabaseField(combinedExams, f.database_field));
      steps.push({
        index: stepIndex++,
        key: `finding-${item.name}-${idx}`,
        label: isMulti ? `${item.name} ${multipleSuffix}` : item.name,
        type: 'structured-finding',
        items: sameName,
        itemIndex: idx,
        isMultiDatabase: isMulti,
      });
    }
  }

  return steps;
};

export const formatStudyDateTime = (exam, studyDate) => {
  if (studyDate && !exam) {
    return formatMultipleDates(studyDate);
  }
  if (exam?.observation_date) {
    try {
      return formatDateSync(exam.observation_date);
    } catch {
      return exam.observation_date;
    }
  }
  if (exam?.study_date) {
    try {
      return formatDateSync(exam.study_date);
    } catch {
      return exam.study_date;
    }
  }
  return '-';
};
